import type { LucideIcon } from 'lucide-react'
import {
  Folder,
  Zap,
  SlidersVertical,
  Files,
  Settings,
  UserCheck,
  Network,
  CalendarCheck,
  ReceiptText,
  Stethoscope,
  DatabaseZap,
  Database,
  HardHat,
  ChartNoAxesColumn,
  PhoneOutgoing,
  MessageSquareText,
  CalendarClock,
  FlaskConical,
  CodeXml,
  Wrench,
  LayoutGrid,
  Cable,
  Server,
  NotebookPen,
  FileText,
  FileSignature,
  Gauge,
  ShieldCheck,
  TestTubeDiagonal,
} from 'lucide-react'
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useShellHref } from '@/utils/router'

import { useCapability } from '@/features/permissions/hooks'
import { useConfiguration } from '@/hooks/useConfiguration'
import { useSnippetsEnabled } from '@/hooks/useSnippetsEnabled'
import { useDutyMetricsEnabled } from '@/hooks/useDutyMetricsEnabled'
import { useEvaluationRubricsEnabled } from '@/hooks/useEvaluationRubricsEnabled'
import { useIsCareforceOrgAdmin } from '@/hooks/useIsCareforceOrgAdmin'
import { useIsGlobalAdmin } from '@/hooks/useIsGlobalAdmin'
import { useCanAdministerPermissions } from '@/hooks/useCanAdministerPermissions'

// Not a visibility gate: it only picks where Notes goes. An org without the
// meeting_notes_table flag or the notes.list capability still sees the row,
// pointed at the legacy app instead.
const NOTES_LEGACY_URL = 'https://legacy.careforce.ai/my-meetings'

export type NavItem = {
  id: string
  label: string
  to: string
  icon: LucideIcon
  /** Shows the AccessLock marker — the rows that carry one today. */
  locked?: boolean
  /** NavLink `end`. */
  end?: boolean
  /** Notes points at the legacy app when the table isn't enabled. */
  external?: boolean
}

export type NavCategory = {
  id: string
  label: string
  icon: LucideIcon
  items: NavItem[]
}

// A plain `.filter((x): x is NavItem => …)` on an inline array literal widens
// `false` to `boolean` during generic inference (TypeScript's usual literal
// widening when a type isn't otherwise fixed), which then fails to narrow.
// Routing through a parameter with a declared type avoids that widening.
function pickItems(items: (NavItem | false)[]): NavItem[] {
  return items.filter((item): item is NavItem => item !== false)
}

/**
 * Categories + items for the rail's flyouts and the mobile drawer.
 *
 * Every gate here is copied verbatim from the pre-redesign AppSidebar — this
 * hook only changes where the destinations render, never who can reach them.
 * `useCapability` is called once per id at the top level (a fixed, static
 * set — never in a loop over the item list), so the existing
 * `vi.mock('@/features/permissions/hooks', { useCapability })` test doubles
 * keep working.
 */
export function useAppSidebarNav(): NavCategory[] {
  const location = useLocation()
  const shellHref = useShellHref()
  const { configuration } = useConfiguration()

  const canClients = useCapability('data.clients.view') === true
  const canVisits = useCapability('data.visits.view') === true
  const canAppointments = useCapability('data.appointments.view') === true
  const canClaims = useCapability('data.claims.view') === true
  const canProviders = useCapability('data.providers.view') === true
  const canFiles = useCapability('data.files.view') === true
  const canIngestion = useCapability('data.ingestion.view') === true
  const canFacts = useCapability('data.facts.view') === true

  const canDuties = useCapability('duties.view') === true
  const canDutiesMetrics = useCapability('duties.metrics.view') === true
  const canCalls = useCapability('calls.list') === true
  const canMessages = useCapability('messages.list') === true
  const canSessions = useCapability('sessions.list') === true

  const canSnippetsView = useCapability('snippets.view') === true
  const canMachines = useCapability('machines.grid') === true

  const canBilling = useCapability('billing.view') === true
  const canConfig = useCapability('config.orgSelect') === true
  const canNotesList = useCapability('notes.list') === true

  const snippetsEnabled = useSnippetsEnabled()
  const dutyMetricsEnabled = useDutyMetricsEnabled()
  const evaluationRubricsEnabled = useEvaluationRubricsEnabled()
  const isCareforceOrgAdmin = useIsCareforceOrgAdmin()
  const isGlobalAdmin = useIsGlobalAdmin()
  const showPermissions = useCanAdministerPermissions()

  const meetingNotesTable =
    canNotesList && (configuration?.features?.meeting_notes_table ?? false)

  const { pathname } = location

  // Memoised because identity matters downstream, not just cost: the flyout
  // keys its focus and placement effects on the category it was handed, and an
  // array rebuilt every render made those effects re-fire on unrelated
  // re-renders (a capability refetch, a Clerk update), stealing focus back to
  // the first row mid-navigation.
  //
  // `shellHref` is itself memoised on the location, so depending on it keeps
  // the memo intact.
  const categories = useMemo<NavCategory[]>(() => {
    return [
      {
        id: 'data',
        label: 'Data',
        icon: Folder,
        items: pickItems([
          canClients
            ? {
                id: 'clients',
                label: 'Clients',
                to: shellHref('/data/patients'),
                icon: UserCheck,
              }
            : false,
          canVisits
            ? {
                id: 'visits',
                label: 'Visits',
                to: shellHref('/data/visits'),
                icon: Network,
              }
            : false,
          canAppointments
            ? {
                id: 'appointments',
                label: 'Appointments',
                to: shellHref('/data/appointments'),
                icon: CalendarCheck,
              }
            : false,
          canClaims
            ? {
                id: 'claims',
                label: 'Claims',
                to: shellHref('/data/claims'),
                icon: ReceiptText,
              }
            : false,
          canProviders
            ? {
                id: 'providers',
                label: 'Providers',
                to: shellHref('/data/providers'),
                icon: Stethoscope,
              }
            : false,
          canFiles
            ? {
                id: 'files',
                label: 'Files',
                to: shellHref('/data/files'),
                icon: Files,
              }
            : false,
          canIngestion
            ? {
                id: 'data-sources',
                label: 'Imports',
                to: shellHref('/data/ingestion'),
                icon: DatabaseZap,
              }
            : false,
          canFacts
            ? {
                id: 'facts',
                label: 'Facts Catalogue',
                to: shellHref('/data/facts'),
                icon: Database,
              }
            : false,
        ]),
      },
      {
        id: 'actions',
        label: 'Actions',
        icon: Zap,
        items: pickItems([
          canDuties
            ? {
                id: 'duties',
                label: 'Duties',
                to: shellHref('/duties'),
                icon: HardHat,
                // `end` only for the sibling page: /duties keeps lighting up
                // on duty detail routes (as /calls and /docs do for theirs),
                // but not for /duties/metrics, which has its own row below —
                // unless that row is hidden, in which case nothing else would
                // be lit.
                end:
                  dutyMetricsEnabled &&
                  canDutiesMetrics &&
                  pathname === '/duties/metrics',
              }
            : false,
          // Matches the route: the org's rollout flag first, then the
          // capability — nested inside the duties.view gate, same as before.
          canDuties && dutyMetricsEnabled && canDutiesMetrics
            ? {
                id: 'metrics',
                label: 'Metrics',
                to: shellHref('/duties/metrics'),
                icon: ChartNoAxesColumn,
              }
            : false,
          canCalls
            ? {
                id: 'calls',
                label: 'Calls',
                to: shellHref('/calls'),
                icon: PhoneOutgoing,
              }
            : false,
          canMessages
            ? {
                id: 'messages',
                label: 'Messages',
                to: shellHref('/messages'),
                icon: MessageSquareText,
                locked: true,
              }
            : false,
          canSessions
            ? {
                id: 'sessions',
                label: 'Sessions',
                to: shellHref('/sessions'),
                icon: CalendarClock,
                locked: true,
              }
            : false,
          isGlobalAdmin
            ? {
                id: 'signing-documents',
                label: 'Documents',
                to: shellHref('/documents'),
                icon: FileSignature,
              }
            : false,
        ]),
      },
      {
        id: 'configs',
        label: 'Configs',
        icon: SlidersVertical,
        items: pickItems([
          isCareforceOrgAdmin
            ? {
                id: 'evals',
                label: 'Evals',
                to: shellHref('/evaluations/memory'),
                icon: FlaskConical,
                locked: true,
              }
            : false,
          snippetsEnabled && canSnippetsView
            ? {
                id: 'snippets',
                label: 'Snippets',
                to: shellHref('/snippets'),
                icon: CodeXml,
              }
            : false,
          isGlobalAdmin
            ? {
                id: 'tools',
                label: 'Tools',
                to: shellHref('/tools'),
                icon: Wrench,
                locked: true,
              }
            : false,
          isCareforceOrgAdmin
            ? {
                id: 'active-lines',
                label: 'Active Lines',
                to: shellHref('/phone-lines'),
                icon: LayoutGrid,
                locked: true,
              }
            : false,
          {
            id: 'connections',
            label: 'Connections',
            to: shellHref('/connections'),
            icon: Cable,
          },
          canMachines
            ? {
                id: 'machines',
                label: 'Machines',
                to: shellHref('/machines'),
                icon: Server,
                locked: true,
              }
            : false,
          evaluationRubricsEnabled
            ? {
                id: 'evaluation-rubrics',
                label: 'Evaluation Rubrics',
                to: shellHref('/evaluation-rubrics'),
                icon: TestTubeDiagonal,
              }
            : false,
        ]),
      },
      {
        id: 'docs',
        label: 'Docs',
        icon: Files,
        items: [
          {
            id: 'notes',
            label: 'Notes',
            icon: NotebookPen,
            to: meetingNotesTable ? shellHref('/notes') : NOTES_LEGACY_URL,
            external: !meetingNotesTable,
          },
          {
            id: 'documents',
            label: 'Documents',
            to: shellHref('/docs'),
            icon: FileText,
          },
        ],
      },
      {
        id: 'settings',
        label: 'Settings',
        icon: Settings,
        items: pickItems([
          canBilling
            ? {
                id: 'usage',
                label: 'Usage',
                to: shellHref('/billing'),
                icon: Gauge,
              }
            : false,
          canConfig
            ? {
                id: 'config',
                label: 'Config',
                to: shellHref('/config'),
                icon: Settings,
                locked: true,
              }
            : false,
          showPermissions
            ? {
                id: 'permissions',
                label: 'Permissions',
                to: shellHref('/permissions'),
                icon: ShieldCheck,
                locked: true,
              }
            : false,
        ]),
      },
    ]
  }, [
    canClients,
    canVisits,
    canAppointments,
    canClaims,
    canProviders,
    canFiles,
    canIngestion,
    canFacts,
    canDuties,
    canDutiesMetrics,
    canCalls,
    canMessages,
    canSessions,
    canSnippetsView,
    canMachines,
    evaluationRubricsEnabled,
    canBilling,
    canConfig,
    snippetsEnabled,
    dutyMetricsEnabled,
    isCareforceOrgAdmin,
    isGlobalAdmin,
    showPermissions,
    meetingNotesTable,
    pathname,
    shellHref,
  ])

  return useMemo(
    () => categories.filter((category) => category.items.length > 0),
    [categories]
  )
}
